<template>
  <!-- 最小化状态：右下角浮动按钮 - 苹果风格 -->
  <transition name="slide-up">
    <div
      v-if="taskStore.panelVisible && taskStore.panelMinimized && taskStore.activeTaskCount > 0"
      class="task-mini-panel"
      :class="{ 'has-error': taskStore.errorTaskCount > 0 }"
      @click="handleExpandPanel"
    >
      <div class="mini-panel-content">
        <div class="icon-wrapper" :class="{ 'icon-wrapper-error': taskStore.errorTaskCount > 0 }">
          <icon-vue
            :icon="taskStore.errorTaskCount > 0 ? 'mdi:alert-circle-outline' : 'mdi:robot-outline'"
            class="robot-icon"
          />
        </div>
        <div class="task-info">
          <div class="task-badge" :class="{ 'task-badge-error': taskStore.errorTaskCount > 0 }">
            {{ taskStore.activeTaskCount }}
          </div>
          <span class="task-label">{{ taskStore.errorTaskCount > 0 ? '个任务需要处理' : '个任务进行中' }}</span>
        </div>
        <div class="expand-indicator">
          <icon-vue icon="mdi:chevron-up" class="chevron-icon" />
        </div>
      </div>
    </div>
  </transition>

  <!-- 展开状态：大抽屉 -->
  <a-drawer
    v-model:open="drawerVisible"
    placement="right"
    :width="taskStore.activeTaskId ? '90%' : 520"
    :closable="!taskStore.activeTaskId"
    :mask="false"
    :mask-closable="false"
    class="ai-task-drawer"
    :destroy-on-close="false"
    @close="handleMinimize"
  >
    <!-- 自定义标题 - 任务列表视图 -->
    <template v-if="!taskStore.activeTaskId" #title>
      <div class="drawer-title">AI 分析任务</div>
    </template>

    <!-- 自定义标题 - 任务详情视图（复刻旧页面样式） -->
    <template v-else #title>
      <task-detail-header
        :key="taskStore.activeTaskId"
        :task="taskStore.activeTask!"
        @minimize="handleMinimize"
        @close="handleClosePanel"
      />
    </template>

    <!-- 任务列表视图的额外操作 -->
    <template v-if="!taskStore.activeTaskId" #extra>
      <div class="flex items-center gap-2">
        <a-button v-if="taskStore.completedTasks.length > 0" size="small" type="text" @click="handleClearCompleted">
          <icon-vue icon="mdi:delete-sweep-outline" class="mr-1" />
          清理已完成
        </a-button>
        <a-button size="small" type="text" @click="handleMinimize">
          <icon-vue icon="mdi:window-minimize" class="mr-1" />
          最小化
        </a-button>
      </div>
    </template>

    <!-- 任务列表视图 -->
    <div v-if="!taskStore.activeTaskId" class="task-list-container">
      <a-tabs v-model:active-key="activeTab" class="task-tabs">
        <a-tab-pane key="active" tab="进行中">
          <div v-if="taskStore.activeTasks.length === 0" class="empty-state">
            <icon-vue icon="mdi:clipboard-text-clock-outline" class="empty-icon" />
            <p class="empty-text">暂无进行中的任务</p>
          </div>
          <div v-else class="task-list">
            <div
              v-for="task in taskStore.activeTasks"
              :key="task.taskId"
              class="task-item"
              :class="{ 'task-item-error': task.status === 'error' }"
              @click="handleViewTask(task.taskId)"
            >
              <div class="task-header">
                <div class="task-title">
                  <h4>{{ task.planName }}</h4>
                  <div class="task-meta">
                    <span>{{ task.projectName }}</span>
                    <span v-if="task.version" class="version-badge">{{ task.version }}</span>
                  </div>
                </div>
                <div class="task-actions">
                  <!-- 错误任务显示重试和忽略按钮 -->
                  <template v-if="task.status === 'error'">
                    <a-button size="small" type="primary" title="重新分析" @click.stop="handleRetryTask(task.taskId)">
                      <icon-vue icon="mdi:refresh" />
                    </a-button>
                    <a-button
                      size="small"
                      type="text"
                      danger
                      title="忽略错误"
                      @click.stop="handleRemoveTask(task.taskId)"
                    >
                      <icon-vue icon="mdi:close-circle-outline" />
                    </a-button>
                  </template>
                  <!-- 正常任务显示取消按钮 -->
                  <a-button
                    v-else
                    size="small"
                    type="text"
                    danger
                    title="取消任务"
                    @click.stop="handleCancelTask(task.taskId)"
                  >
                    <icon-vue icon="mdi:close-circle-outline" />
                  </a-button>
                </div>
              </div>
              <!-- 错误任务显示错误信息 -->
              <div v-if="task.status === 'error'" class="task-error">
                <a-alert type="error" :message="task.error || '分析失败'" show-icon banner />
              </div>
              <!-- 正常任务显示进度条 -->
              <div v-else class="task-progress">
                <a-progress
                  :percent="task.progress"
                  :status="getProgressStatus(task.status)"
                  :show-info="true"
                  stroke-color="#667eea"
                />
              </div>
              <div class="task-info">
                <div class="task-stage">
                  <icon-vue icon="mdi:pulse" class="stage-icon" />
                  <span>{{ getStageText(task.stage) }}</span>
                </div>
                <div class="task-time">
                  <icon-vue icon="mdi:clock-outline" class="time-icon" />
                  <span>{{ getTaskDuration(task) }}</span>
                </div>
              </div>
              <div v-if="task.message && task.status !== 'error'" class="task-message">{{ task.message }}</div>
            </div>
          </div>
        </a-tab-pane>

        <a-tab-pane key="completed" :tab="`已完成 (${taskStore.completedTasks.length})`">
          <div v-if="taskStore.completedTasks.length === 0" class="empty-state">
            <icon-vue icon="mdi:check-circle-outline" class="empty-icon" />
            <p class="empty-text">暂无已完成的任务</p>
          </div>
          <div v-else class="task-list">
            <div
              v-for="task in taskStore.completedTasks"
              :key="task.taskId"
              class="task-item completed"
              @click="handleViewTask(task.taskId)"
            >
              <div class="task-header">
                <div class="task-title">
                  <h4>{{ task.planName }}</h4>
                  <div class="task-meta">
                    <span>{{ task.projectName }}</span>
                    <span v-if="task.version" class="version-badge">{{ task.version }}</span>
                  </div>
                </div>
                <div class="task-actions">
                  <a-button size="small" type="text" title="重新分析" @click.stop="handleRetryTask(task.taskId)">
                    <icon-vue icon="mdi:refresh" />
                  </a-button>
                  <a-button size="small" type="text" danger title="删除" @click.stop="handleRemoveTask(task.taskId)">
                    <icon-vue icon="mdi:delete-outline" />
                  </a-button>
                </div>
              </div>
              <div class="task-result">
                <a-tag v-if="task.status === 'completed'" color="success">
                  <icon-vue icon="mdi:check-circle" class="mr-1" />
                  分析完成
                </a-tag>
                <a-tag v-else-if="task.status === 'error'" color="error">
                  <icon-vue icon="mdi:alert-circle" class="mr-1" />
                  分析失败
                </a-tag>
              </div>
              <div class="task-info">
                <div class="task-time">
                  <icon-vue icon="mdi:clock-outline" class="time-icon" />
                  <span>{{ formatTime(task.startTime) }}</span>
                </div>
                <div class="task-duration">
                  <span>耗时 {{ getTaskDuration(task) }}</span>
                </div>
              </div>
            </div>
          </div>
        </a-tab-pane>
      </a-tabs>
    </div>

    <!-- 任务详情视图 -->
    <div v-else class="task-detail">
      <!-- 嵌入 AI 分析内容组件 -->
      <a-i-analysis-content
        v-if="taskStore.activeTask"
        :key="taskStore.activeTaskId"
        :task-id="taskStore.activeTaskId!"
        :task="taskStore.activeTask"
        @minimize="handleMinimize"
        @update:task="handleUpdateTask"
      />
    </div>
  </a-drawer>
</template>

<script setup lang="ts">
import { useAIAnalysis } from '@/sites/precision-new/composables/useAIAnalysis';
import { useAIAnalysisTasksStore, type AIAnalysisTask } from '@/store/modules/ai-analysis-tasks';
import type { AnalysisStage, AnalysisStatus } from '@/types/precision-new/ai-analysis';
import { Icon as IconVue } from '@iconify/vue';
import { message } from 'ant-design-vue';
import dayjs from 'dayjs';
import { computed, provide, ref } from 'vue';
import AIAnalysisContent from './AIAnalysisContent.vue';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import TaskDetailHeader from './TaskDetailHeader.vue';

const taskStore = useAIAnalysisTasksStore();
const activeTab = ref<'active' | 'completed'>('active');

// 创建共享的 AI 分析实例，提供给子组件使用
const sharedAIAnalysis = useAIAnalysis();
provide('aiAnalysis', sharedAIAnalysis);

const drawerVisible = computed({
  get: () => taskStore.panelVisible && !taskStore.panelMinimized,
  set: (val) => {
    if (!val) {
      handleMinimize();
    }
  },
});

function handleExpandPanel() {
  taskStore.maximizePanel();
}

function handleMinimize() {
  taskStore.minimizePanel();
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function handleClosePanel() {
  taskStore.closePanel();
}

function handleViewTask(taskId: string) {
  taskStore.showTaskDetail(taskId);
}

function handleCancelTask(taskId: string) {
  taskStore.cancelTask(taskId);
  message.info('已取消分析任务');
}

function handleRemoveTask(taskId: string) {
  taskStore.removeTask(taskId);
  message.success('任务已删除');
}

function handleRetryTask(taskId: string) {
  const params = taskStore.retryTask(taskId);
  if (params) {
    message.info('正在重新开始分析...');
    handleViewTask(taskId);
  } else {
    message.error('无法重试该任务');
  }
}

function handleClearCompleted() {
  taskStore.clearCompletedTasks();
  message.success('已清理完成的任务');
}

function handleUpdateTask(taskId: string, updates: Partial<AIAnalysisTask>) {
  taskStore.updateTask(taskId, updates);
}

function getProgressStatus(status: AnalysisStatus) {
  const statusMap: Record<string, any> = {
    analyzing: 'active',
    completed: 'success',
    error: 'exception',
    idle: 'normal',
    paused: 'normal',
  };
  return statusMap[status] || 'normal';
}

function getStageText(stage: AnalysisStage): string {
  const stageMap: Record<AnalysisStage, string> = {
    data_collection: '收集数据',
    git_api: '获取代码变更',
    database: '查询测试用例',
    ai_analysis: 'AI分析中',
    ai_thinking: 'AI思考中',
    ai_calling: 'AI调用中',
    processing_stream: '处理响应',
    parsing_result: '解析结果',
    finalizing: '生成报告',
    completed: '已完成',
    error: '出错',
  };
  return stageMap[stage] || stage;
}

function getTaskDuration(task: AIAnalysisTask): string {
  const endTime = task.endTime || Date.now();
  const durationMs = endTime - task.startTime;
  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}小时${minutes % 60}分`;
  } else if (minutes > 0) {
    return `${minutes}分${seconds % 60}秒`;
  } else {
    return `${seconds}秒`;
  }
}

function formatTime(timestamp: number): string {
  return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
}
</script>

<style scoped>
/* 最小化浮动面板 - 苹果风格毛玻璃效果 */
.task-mini-panel {
  position: fixed;
  bottom: 28px;
  right: 28px;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: saturate(180%) blur(20px);
  -webkit-backdrop-filter: saturate(180%) blur(20px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  padding: 12px 18px;
  border-radius: 20px;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.08),
    0 4px 16px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
  min-width: 200px;
}

/* 错误状态的 mini-panel */
.task-mini-panel.has-error {
  background: rgba(255, 245, 245, 0.85);
  border-color: rgba(255, 77, 79, 0.2);
  box-shadow:
    0 8px 32px rgba(255, 77, 79, 0.12),
    0 4px 16px rgba(255, 77, 79, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
}

.task-mini-panel:hover {
  transform: translateY(-4px) scale(1.02);
  background: rgba(255, 255, 255, 0.85);
  border-color: rgba(0, 122, 255, 0.2);
  box-shadow:
    0 12px 40px rgba(0, 122, 255, 0.15),
    0 6px 20px rgba(0, 0, 0, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

.task-mini-panel.has-error:hover {
  background: rgba(255, 245, 245, 0.95);
  border-color: rgba(255, 77, 79, 0.3);
  box-shadow:
    0 12px 40px rgba(255, 77, 79, 0.2),
    0 6px 20px rgba(255, 77, 79, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

.task-mini-panel:active {
  transform: translateY(-2px) scale(0.99);
  transition-duration: 0.1s;
}

.mini-panel-content {
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
}

/* 图标包装器 */
.icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #40a9ff, #1890ff);
  border-radius: 10px;
  box-shadow:
    0 2px 8px rgba(24, 144, 255, 0.25),
    0 1px 3px rgba(24, 144, 255, 0.15);
  flex-shrink: 0;
  transition: all 0.3s ease;
}

/* 错误状态的图标包装器 */
.icon-wrapper-error {
  background: linear-gradient(135deg, #ff4d4f, #ff7875);
  box-shadow:
    0 2px 8px rgba(255, 77, 79, 0.3),
    0 1px 3px rgba(255, 77, 79, 0.2);
  animation: pulse-error 2s ease-in-out infinite;
}

@keyframes pulse-error {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.9;
    transform: scale(1.05);
  }
}

.robot-icon {
  font-size: 18px;
  color: #ffffff;
  animation: pulse-gentle 2s ease-in-out infinite;
}

@keyframes pulse-gentle {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.85;
    transform: scale(1.05);
  }
}

/* 任务信息 */
.task-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

/* iOS风格红色徽章 */
.task-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: linear-gradient(135deg, #40a9ff, #1890ff);
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  border-radius: 10px;
  box-shadow:
    0 2px 6px rgba(24, 144, 255, 0.3),
    0 1px 2px rgba(24, 144, 255, 0.2);
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', sans-serif;
  letter-spacing: -0.2px;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

/* 错误状态的任务徽章 */
.task-badge-error {
  background: linear-gradient(135deg, #ff4d4f, #ff7875);
  box-shadow:
    0 2px 6px rgba(255, 77, 79, 0.4),
    0 1px 2px rgba(255, 77, 79, 0.3);
  animation: pulse-badge-error 2s ease-in-out infinite;
}

@keyframes pulse-badge-error {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.task-label {
  font-size: 13px;
  font-weight: 500;
  color: #1d1d1f;
  white-space: nowrap;
}

/* 展开指示器 */
.expand-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 6px;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.chevron-icon {
  font-size: 14px;
  color: #86868b;
  transition: all 0.3s ease;
}

.task-mini-panel:hover .expand-indicator {
  background: rgba(0, 122, 255, 0.1);
}

.task-mini-panel:hover .chevron-icon {
  color: #007aff;
  transform: translateY(-2px);
}

/* 任务列表容器 */
.task-list-container {
  height: calc(100vh - 120px);
  overflow: hidden;
}

.task-tabs :deep(.ant-tabs-content) {
  height: calc(100vh - 180px);
  overflow-y: auto;
}

/* 任务列表 */
.task-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 0;
}

.task-item {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

/* 错误状态的任务卡片 */
.task-item-error {
  background: linear-gradient(135deg, #fff5f5 0%, #ffe7e7 100%);
  border: 2px solid #ff4d4f;
  box-shadow:
    0 4px 12px rgba(255, 77, 79, 0.15),
    0 2px 6px rgba(255, 77, 79, 0.1);
}

.task-item-error::before {
  width: 6px;
  background: linear-gradient(180deg, #ff4d4f 0%, #ff7875 100%);
  transform: scaleY(1);
}

.task-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
  transform: scaleY(0);
  transition: transform 0.3s ease;
}

.task-item:hover::before {
  transform: scaleY(1);
}

.task-item:hover {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-color: #667eea;
  transform: translateX(4px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
}

.task-item.completed {
  opacity: 0.9;
}

.task-item.completed:hover {
  opacity: 1;
}

.task-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.task-title h4 {
  margin: 0 0 6px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.task-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #64748b;
}

.version-badge {
  padding: 2px 8px;
  background: #e0e7ff;
  color: #4f46e5;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.task-actions {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.task-item:hover .task-actions {
  opacity: 1;
}

.task-progress {
  margin-bottom: 12px;
}

.task-error {
  margin-bottom: 12px;
}

.task-error :deep(.ant-alert) {
  border-radius: 8px;
}

.task-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #64748b;
}

.task-stage,
.task-time,
.task-duration {
  display: flex;
  align-items: center;
  gap: 6px;
}

.stage-icon,
.time-icon {
  width: 14px;
  height: 14px;
  color: #667eea;
}

.task-message {
  margin-top: 8px;
  padding: 8px 12px;
  background: #f1f5f9;
  border-radius: 6px;
  font-size: 12px;
  color: #475569;
  font-style: italic;
}

.task-result {
  margin-bottom: 8px;
}

/* 空状态 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #94a3b8;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-text {
  font-size: 14px;
  margin: 0;
}

/* 任务详情 */
.task-detail {
  display: flex;
  flex-direction: column;
}

/* 抽屉自定义样式 */
:deep(.ai-task-drawer .ant-drawer-header) {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-bottom: 2px solid #e9ecef;
}

:deep(.ai-task-drawer .ant-drawer-title) {
  font-weight: 600;
  color: #1e293b;
}

:deep(.ai-task-drawer .ant-drawer-body) {
  padding: 16px 24px;
  background: #fafbfc;
}

/* 动画 - 苹果风格弹性动画 */
.slide-up-enter-active {
  animation: slide-up-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.slide-up-leave-active {
  animation: slide-up-out 0.3s cubic-bezier(0.4, 0, 1, 1);
}

@keyframes slide-up-in {
  0% {
    transform: translateY(100px) scale(0.8);
    opacity: 0;
  }
  50% {
    transform: translateY(-5px) scale(1.02);
  }
  100% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

@keyframes slide-up-out {
  0% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
  100% {
    transform: translateY(100px) scale(0.85);
    opacity: 0;
  }
}

/* 响应式 - 移动端优化 */
@media (max-width: 768px) {
  .task-mini-panel {
    bottom: 20px;
    right: 20px;
    padding: 10px 16px;
    border-radius: 18px;
    min-width: 180px;
  }

  .mini-panel-content {
    gap: 10px;
  }

  .icon-wrapper {
    width: 28px;
    height: 28px;
    border-radius: 8px;
  }

  .robot-icon {
    font-size: 16px;
  }

  .task-badge {
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    font-size: 11px;
    border-radius: 9px;
  }

  .task-label {
    font-size: 12px;
  }

  .expand-indicator {
    width: 18px;
    height: 18px;
  }

  .chevron-icon {
    font-size: 12px;
  }

  :deep(.ant-drawer) {
    width: 100% !important;
  }
}

/* 深色模式支持（苹果风格） */
@media (prefers-color-scheme: dark) {
  .task-mini-panel {
    background: rgba(30, 30, 30, 0.8);
    border-color: rgba(255, 255, 255, 0.12);
    box-shadow:
      0 8px 32px rgba(0, 0, 0, 0.5),
      0 4px 16px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.08);
  }

  .task-mini-panel:hover {
    background: rgba(40, 40, 40, 0.85);
    border-color: rgba(0, 122, 255, 0.4);
    box-shadow:
      0 12px 40px rgba(0, 122, 255, 0.25),
      0 6px 20px rgba(0, 0, 0, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.12);
  }

  .task-badge {
    background: linear-gradient(135deg, #ff453a, #ff5c54);
    box-shadow:
      0 2px 6px rgba(255, 69, 58, 0.4),
      0 1px 2px rgba(255, 69, 58, 0.3);
  }

  .task-label {
    color: #f5f5f7;
  }

  .expand-indicator {
    background: rgba(255, 255, 255, 0.08);
  }

  .chevron-icon {
    color: #a1a1a6;
  }

  .task-mini-panel:hover .expand-indicator {
    background: rgba(0, 122, 255, 0.2);
  }

  .task-mini-panel:hover .chevron-icon {
    color: #40a9ff;
  }
}
</style>
